import React from 'react';
import { connect } from 'react-redux';
import { reduxForm, Field, formValueSelector, change } from 'redux-form';
import classnames from 'classnames';
import { Button, InputGroup } from 'reactstrap';
import { getUpdatedTextWithEmoji } from '../../../../../../../common/components/EmojiPicker/emoji-picker-helpers';
import { EmojiPicker } from '../../../../../../../common/components/EmojiPicker/EmojiPicker';
import {
  generatePageStructure,
  nameById,
} from '../../../../../../../common/components/FilterableSelect/filterable-select-helpers';
import { ENTER_CODE } from "@mm/constants/keys-codes";
import {
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '../../../../../../../common/modals/modal-helpers';
import { ButtonType } from "@mm/state/model/ButtonModel";
import TextInput from '../../../../../../../common/components/TextInput';
import Select from '../../../../../../../common/components/FilterableSelect/FilterableSelect';
import '../ButtonConfigform/ButtonConfigForm.scss';
import '../../../../../../../common/scss/input.scss';
import { validateButtonConfigForm } from '../ButtonConfigform/validations';
import { PageKind } from "@mm/state/model/PageModel";

const TypeSelector = props => {
  const { buttons, selectedButtonIndex, setInitialValues } = props;

  return buttons.map((button, index) => {
    const selected = index === selectedButtonIndex;
    const classList = classnames('button-config__button', {
      'button-config__type-button--selected': selected,
      'button-config__type-button': !selected,
    });

    return (
      <Button
        key={index}
        onClick={() => {
          props.input.onChange(index);
          setInitialValues();
        }}
        disabled={selected}
        color="warning"
        outline={!selected}
        className={classList}
      >
        {button}
      </Button>
    );
  });
};

const PAGE_BUTTON_INDEX = 0;
const URL_BUTTON_INDEX = 1;

const getIndexByType = new Map([
  [ButtonType.PAGE, PAGE_BUTTON_INDEX],
  [ButtonType.URL, URL_BUTTON_INDEX],
]);

const getTypeByIndex = new Map([
  [PAGE_BUTTON_INDEX, ButtonType.PAGE],
  [URL_BUTTON_INDEX, ButtonType.URL],
]);

class MenuButtonConfigForm extends React.Component {
  static buttons = [
    <span>
      <i className="far fa-file-alt pr-2" aria-hidden="true" />
      &nbsp;Dialogue
    </span>,
    <span>
      <i className="fas fa-external-link-alt pr-2" aria-hidden="true" />
      &nbsp;URL
    </span>,
  ];

  nameInputRef = null;

  UNSAFE_componentWillMount() {
    const { button, changeFieldValue } = this.props;

    document.addEventListener('keydown', this.onButtonConfigFormKeyDown);

    const welcomePage = this.getWelcomePage();
    const selectedButtonIndex = getIndexByType.get(button.type);

    changeFieldValue('selectedButtonIndex', selectedButtonIndex);

    changeFieldValue('name', button.name);
    changeFieldValue('url', button.config.url || '');
    changeFieldValue(
      'page',
      button.config.pageId !== -1 ? button.config.pageId : welcomePage.id,
    );
    changeFieldValue('phone', button.config.phoneNumber || '');
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onButtonConfigFormKeyDown);
  }

  getInitialValues = () => ({
    page: this.getWelcomePage().id,
    phone: '',
    url: '',
  });

  getWelcomePage = () =>
    this.props.pages.find(page => page.kind === PageKind.WelcomePage);

  onButtonConfigFormKeyDown = e => {
    if (e.keyCode === ENTER_CODE && this.props.valid) {
      this.onCreateButton();
      e.preventDefault();
    }
  };

  reduxFormInput = ({
    input: { onChange, onBlur, onFocus, value },
    ...props
  }) => (
    <TextInput
      {...props}
      value={value}
      onChange={onChange}
      onFocus={onFocus}
      onBlur={onBlur}
    />
  );

  reduxSelectInput = ({ input: { onChange, value }, children, ...props }) => (
    <Select
      {...props}
      onChange={onChange}
      outerValue={nameById(value, this.props.pages)}
    >
      {children}
    </Select>
  );

  renderButtonPicker() {
    switch (this.props.selectedButtonIndex) {
      case PAGE_BUTTON_INDEX: {
        const suggestions = generatePageStructure(
          this.props.pages,
          this.props.pageGroups,
        );

        return (
          <Field
            className="chatbot__input"
            name="page"
            component={this.reduxSelectInput}
            suggestions={suggestions}
          />
        );
      }
      case URL_BUTTON_INDEX: {
        return (
          <InputGroup className="button-config__input">
            <Field
              name="url"
              type="url"
              component={this.reduxFormInput}
              placeholder="Enter url"
            />
          </InputGroup>
        );
      }
      default:
        return null;
    }
  }

  setInitialValues = () => {
    const initialState = this.getInitialValues();
    Object.entries(initialState).forEach(([fieldName, value]) =>
      this.props.changeFieldValue(fieldName, value),
    );
  };

  onCreateButton = () => {
    const index = this.props.selectedButtonIndex;
    let button = null;
    try {
      const { name, sortOrder, url, page } = this.props;

      button = {
        type: getTypeByIndex.get(index),
        name,
        sortOrder,
        config: {
          url,
          pageId: page,
        },
        id: this.props.button.id,
      };
    } catch (e) {
      button = {};
    }

    const buttonIndex = Number(this.props.buttonIndex);

    if (buttonIndex === -1) {
      this.props.onCreateButton(button);
    } else {
      this.props.onUpdateButton(this.props.buttonIndex, button);
    }
  };

  ref = node => {
    if (!node) return;

    this.buttonName = node;
  };

  updateInsertEmoji = emoji => {
    const { changeFieldValue, name } = this.props;

    const newText = getUpdatedTextWithEmoji(name, emoji, this.nameInputRef);

    changeFieldValue('name', newText);
  };

  render() {
    const { closeModal, buttonIndex, valid, closeModalOverride } = this.props;
    const hasButton = Number(buttonIndex) !== -1;
    return (
      <div className="button-config__menu-button-config-modal">
        <ModalHeader
          className="button-config__modal-header"
          closeOverride={closeModalOverride}
        >
          {hasButton ? 'Edit Button' : 'Add Button'}
        </ModalHeader>
        <ModalBody className="menu-button-config-form__body">
          <div className="button-config">
            <div className="mb-3">
              <InputGroup className="button-config__input button-config__name-input">
                <Field
                  className="button-config__name-field"
                  name="name"
                  type="text"
                  maxLength="20"
                  inputRef={ref => {
                    this.nameInputRef = ref;
                  }}
                  component={this.reduxFormInput}
                  placeholder="Enter button name"
                />

                <EmojiPicker
                  onEmojiPicked={this.updateInsertEmoji}
                  className="button-config__emoji"
                />
              </InputGroup>
            </div>
            <div className="mb-3">
              <Field
                name="selectedButtonIndex"
                component={TypeSelector}
                selectedButtonIndex={this.props.selectedButtonIndex}
                setInitialValues={this.setInitialValues}
                buttons={MenuButtonConfigForm.buttons}
              />
            </div>
            <div className="mb-3">{this.renderButtonPicker()}</div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            outline
            color="danger"
            size="lg"
            onClick={() => closeModalOverride?.() || closeModal()}
            className="button-config__button"
          >
            Cancel
          </Button>
          <Button
            color="warning"
            onClick={this.onCreateButton}
            className="btn-lg button-config__button button-config__button--add"
            disabled={!valid}
          >
            <i className="fa fa-plus-circle fa-lg bg-warning" />
            &nbsp;{hasButton ? 'Update Button' : 'Add Button'}
          </Button>
        </ModalFooter>
      </div>
    );
  }
}

const MenuButtonConfigReduxForm = reduxForm({
  form: 'buttonConfig',
  validate: validateButtonConfigForm,
})(MenuButtonConfigForm);

const selector = formValueSelector('buttonConfig');

const mapStateToProps = state => {
  const { url, phone, name, page, pageGroup, selectedButtonIndex } = selector(
    state,
    'url',
    'phone',
    'name',
    'page',
    'pageGroup',
    'selectedButtonIndex',
  );

  return {
    url,
    phone,
    name,
    page,
    pageGroup,
    selectedButtonIndex,
    pageGroups: state.pageGroup.entities,
    pages: state.page.dropdown,
  };
};

const mapDispatchToProps = dispatch => ({
  changeFieldValue: (field, value) =>
    dispatch(change('buttonConfig', field, value)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(MenuButtonConfigReduxForm);
